import { augmentArtists, mapArtistPageContentToArtist } from '@utils/artists';
import cache from '@utils/cache';
import { parseDate } from '@utils/datetime';
import {
  fetchGlossary,
  GlossaryLink,
  mapGlossaryLinks,
  mapGlossaryTerms,
  richTextWithGlossary,
} from '@utils/glossary';
import { shopifyToStoryblokDate } from '@utils/product';
import { fetchAllProductsCount } from '@utils/shopify/fetchAllProducts';
import {
  getStoryblokContentBySlug,
  getStoryblokContentByUuids,
  getStoryblokFaq,
  useStoryblok,
} from '@utils/storyblok';
import { isProductContentV2 } from '@utils/storyblok/stories';
import { pageTranslations } from '@utils/translations';
import { isAfter } from 'date-fns';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer-ts';

import { csrClient, ssgClient } from '../../apollo/api/avantArteClient';
import { storefrontClient } from '../../apollo/shopify/storefrontClient';
import { Provider as ManifoldProvider } from '../../components/Auction/Manifold/Provider';
import { GlossaryContextProvider } from '../../components/Glossary/GlossaryContext';
import Layout from '../../components/Layout';
import Product from '../../components/PageTypes/Product';
import ProductV2 from '../../components/PageTypes/ProductV2';
import { PreviewWarningProducts } from '../../components/PreviewWarnings/PreviewWarningProducts';
import Web3ContextProvider from '../../components/Web3/Web3Provider/Web3Provider';
import {
  FIFTEEN_MINUTES_IN_SECONDS,
  PRODUCTS_CONTENT_TYPE_BLOCK_V2,
  PRODUCTS_PAGE_ROOT_PATH,
  WORK_PAGE_TYPE,
} from '../../constants';
import {
  FIRST_SUPPORTED_DRAW_LAUNCH,
  PRODUCT_TAG_COMING_SOON,
  PRODUCT_TAG_MEDIUM_COLLECTIBLE,
  PRODUCT_TAG_MEDIUM_NFT,
  PRODUCT_TAG_MEDIUM_PRINT,
  PRODUCT_TAG_MEDIUM_SCULPTURE,
  PRODUCT_TAG_MEDIUM_STUDIO_WORKS,
  PRODUCT_TAG_NO_COA,
  PRODUCT_TAG_SOLD_OUT,
  PRODUCT_VARIANT_TITLE_PUBLIC,
} from '../../constants/product';
import { AvantArteDrawGateway } from '../../features/draws/gateways/AvantArteDrawGateway';
import { AvailableDraw, DrawWithFrames } from '../../features/draws/types';
import { AvantArteArtistsSearchGateway } from '../../gateways/AvantArteArtistsSearchGateway';
import { AvantArteProductGateway } from '../../gateways/AvantArteProductGateway';
import { ShopifyProductRecommendationsGateway } from '../../gateways/ShopifyProductRecommendationGateway';
import { ShopifyProductsGateway } from '../../gateways/ShopifyProductsGateway';
import { toCanonicalUrl } from '../../helpers/links';
import { DrawProvider } from '../../providers/DrawProvider';
import { PreAuthDrawProvider } from '../../providers/PreAuthDrawProvider';
import { PurchaseProvider } from '../../providers/PurchaseProvider';
import { AugmentedArtistCard } from '../../types/Artists';
import {
  GeneratedGetProductsQuery,
  GeneratedReview,
  queryReviews,
} from '../../types/generated';
import {
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../types/generated-storyblok';
import { GlossaryItem } from '../../types/Glossary';
import {
  ProductPurchaseDetails,
  ShopifyProductPurchaseDetails,
} from '../../types/Purchasing';

const shopifyProductsGateway = new ShopifyProductsGateway(storefrontClient);
const aaProductsGateway = new AvantArteProductGateway(csrClient);
const drawGateway = new AvantArteDrawGateway(csrClient);

type ProductPageData = {
  description: StoryblokTextAreaType;
  glossary: GlossaryItem[];
  pricesAndAvailability: Array<PriceAndCurrency>;
  productGlossaryTerms: GlossaryLink[];
  augmentedArtists: AugmentedArtistCard[];
  recommendations: MultiCurrencyProduct[];
  worksCount: number;
  taxText: StoryblokTextAreaType;
  reviews: GeneratedReview[];
  draws: {
    mainDraw?: AvailableDraw;
    linkedDraws: AvailableDraw[];
  };
};

interface Props extends PageProps {
  version: 1 | 2;
  slug: string;
  productDetails: ProductPurchaseDetails | null;
  aaProduct: GeneratedGetProductsQuery['getProducts'][number] | null;
}

const SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER = {
  UNSPECIFIED: '',
  PUBLIC: 'public-release',
  DRAW: 'draw-release',
  PRIVATE: 'private-release',
  TIMED: 'timed-release',
  AUCTION: 'auction',
  BLOCKCHAIN_AUCTION: 'blockchain_auction',
  NFT_GIVEAWAY: 'nft_giveaway',
  NFT_RANKED_AUCTION: 'nft_ranked_auction',
} as const;

const shopifyToStoryblokReleaseType = (
  key: keyof typeof SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER,
) => SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER[key];

const PageContent = ({
  aaProduct,
  locale,
  locales,
  ogImageUrl,
  pageData,
  preview,
  productDetails,
  slug,
  story,
  version,
}: Props) => {
  story = useStoryblok(story, preview, locale);
  slug = slug.endsWith('-ja') ? slug.slice(0, -3) : slug;

  // Canonicalise to the clean English product URL so the Japanese variant
  // is never indexed as a separate page.
  const canonicalUrl = toCanonicalUrl({
    path: `/${PRODUCTS_PAGE_ROOT_PATH}/${slug}`,
    hostname: process.env.NEXT_PUBLIC_HOSTNAME!,
    locale: 'en',
  });

  const data = pageData as ProductPageData;
  const variantPublic = aaProduct?.variants.find(
    (v) => v.title.toLowerCase() === PRODUCT_VARIANT_TITLE_PUBLIC.toLowerCase(),
  );

  const content: ProductType = {
    ...story.content,
    aaId: aaProduct?.id,
    shopifyVariantId: productDetails?.shopifyVariantId,
    description: data.description,
    recommendations: data.recommendations || [],
    worksCount: data.worksCount,
    taxText: data.taxText,
    pricesAndAvailability: data.pricesAndAvailability,
    offersDisabled: true, // We are disabling all offers for now
    ...(version === 2
      ? {
          nftAuction: [
            {
              ...((story.content as ProductV2Storyblok).nftAuction?.[0] || []),
              manifoldListingId: productDetails?.manifoldListingId,
              floorPrice: productDetails?.auctionFloorPrice,
            },
          ],
          title: aaProduct?.title,
          thumbnail: (story.content as ProductV2Storyblok).ogImage,
          seo: {
            title: (story.content as ProductV2Storyblok).seoTitle,
            description: (story.content as ProductV2Storyblok).seoDescription,
          },
          id: [{ id: (story.content as ProductV2Storyblok).shopifyId }],
          noCOA: productDetails?.tags.includes(PRODUCT_TAG_NO_COA) ?? false,
          details: (story.content as ProductV2Storyblok).details,
          behindTheScenesContainer: [],
          fromTheExpertsContainer: [],
          fromTheArtistContainer: [],
          productArtist: (story.content as ProductV2Storyblok).artists,
          ...(aaProduct?.launchAt && {
            launchDate: shopifyToStoryblokDate(aaProduct?.launchAt),
          }),
          editionSize: aaProduct?.editionSize,
          ...(aaProduct?.closeAt && {
            closingDate: shopifyToStoryblokDate(aaProduct?.closeAt),
          }),
          ...(aaProduct?.releaseType && {
            releaseType: shopifyToStoryblokReleaseType(aaProduct.releaseType),
          }),
          ...(aaProduct?.price && {
            price:
              variantPublic?.price.toString() || aaProduct.price.toString(),
          }),
          ...(aaProduct?.releaseType && {
            isDrawLaunch: aaProduct?.drawLaunch,
          }),
          drawReleaseIsPreAuth: true,
          productVariants: (
            story.content as ProductV2Storyblok
          )?.productVariants?.map((productVariant) => ({
            ...productVariant,
            variants: productVariant.variants?.map((productVariantItem) => ({
              ...productVariantItem,
              content: {
                ...productVariantItem.content,
                id: [{ id: productVariantItem.content.shopifyId }],
              },
            })),
          })),
        }
      : {}),
  };

  return (
    <Layout
      canonicalUrl={canonicalUrl}
      description={content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={content.seo?.title}
    >
      <ManifoldProvider>
        <PreviewWarningProducts
          api={aaProduct ?? undefined}
          preview={preview}
          shopify={productDetails}
          slug={slug}
          storyblok={content}
        />
        <GlossaryContextProvider
          analytics={{
            context: 'product description',
            page: WORK_PAGE_TYPE,
          }}
          glossary={data.glossary}
        >
          <PurchaseProvider
            backendProduct={aaProduct}
            product={content}
            productDetails={productDetails}
          >
            {version === 2 ? (
              <DrawProvider
                drawGateway={drawGateway}
                linkedDraws={data.draws.linkedDraws}
                mainDraw={data.draws.mainDraw}
              >
                <PreAuthDrawProvider>
                  <ProductV2
                    aaProductsGateway={aaProductsGateway}
                    augmentedArtists={data.augmentedArtists}
                    blok={content}
                    productDetails={productDetails}
                    recommendations={(pageData as any).recommendations}
                    reviews={data.reviews}
                  />
                </PreAuthDrawProvider>
              </DrawProvider>
            ) : (
              <Product
                aaProductsGateway={aaProductsGateway}
                augmentedArtists={data.augmentedArtists}
                blok={content}
                draw={(pageData as any).draw}
                drawGateway={drawGateway}
                productDetails={productDetails}
                productGlossaryTerms={data.productGlossaryTerms}
                recommendations={(pageData as any).recommendations}
                reviews={data.reviews}
                slug={slug}
              />
            )}
          </PurchaseProvider>
        </GlossaryContextProvider>
      </ManifoldProvider>
    </Layout>
  );
};

export default function Page(props: Props) {
  const isGatedRelease =
    (props.story.content as ProductType).gatedRelease?.[0]?.active ?? false;

  return isGatedRelease ? (
    <Web3ContextProvider>
      <PageContent {...props} />
    </Web3ContextProvider>
  ) : (
    <PageContent {...props} />
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale, locales, params, preview = false } = ctx;
  const paramsSlug = params?.slug
    ? (params.slug as string[]).join('/')
    : 'home';
  const isJa = paramsSlug.endsWith('-ja');
  const slug = isJa ? paramsSlug.slice(0, -3) : paramsSlug;

  const client = ssgClient(ctx);
  const aaProductsGatewayServer = new AvantArteProductGateway(client);

  const [
    responseProductsApiBySlug,
    responseProductStoryblok,
    responseStoryblokFaq,
    responseReviews,
  ] = await Promise.all([
    aaProductsGatewayServer.getProducts([], [], [slug]),
    getStoryblokContentBySlug(
      PRODUCTS_PAGE_ROOT_PATH,
      slug,
      isJa ? 'ja' : locale,
      preview,
    ),
    getStoryblokFaq('shipping', '7f60f8ef-43c3-4a9e-a442-f886289eb5b3', {
      locale,
      preview,
    }),
    queryReviews(client, {}),
  ]);

  let variants = null;
  if (
    responseProductStoryblok?.data?.story?.content?.productVariants.length > 0
  ) {
    const variantsIds = responseProductStoryblok?.data?.story?.content
      ?.productVariants[0].variants as string[]; // only one variant list is allowed in storyblok so it's ok to get the first
    variants = await getStoryblokContentByUuids(
      variantsIds,
      isJa ? 'ja' : locale,
      preview,
    );
  }

  const contentProductStoryblok:
    | (ProductStoryblok & { [k: string]: any })
    | (ProductV2Storyblok & { [k: string]: any }) =
    responseProductStoryblok?.data?.story?.content;

  if (!contentProductStoryblok) {
    return {
      notFound: true,
    };
  }

  const { isPrivateOffer } = contentProductStoryblok;

  if (isPrivateOffer && !preview) {
    return {
      notFound: true,
    };
  }

  const isV2 = isProductContentV2(contentProductStoryblok);

  // pre-fetch the product from shopify
  // and pass as a default value to the purchaseController
  let productDetails: ShopifyProductPurchaseDetails | undefined | null;

  const shopifyId = isV2
    ? contentProductStoryblok.shopifyId
    : contentProductStoryblok.id?.[0]?.id;

  if (shopifyId) {
    productDetails = await shopifyProductsGateway.getProductPurchaseDetails({
      identifier: { id: shopifyId },
      releaseName: null,
      variantIdentifier: undefined,
    });
  }

  let mainDraw: DrawWithFrames | null = null;
  const linkedDraws: DrawWithFrames[] = [];

  const launchDate = isV2
    ? shopifyToStoryblokDate(responseProductsApiBySlug[0]?.launchAt ?? '')
    : contentProductStoryblok.launchDate;
  const releaseType = isV2
    ? shopifyToStoryblokReleaseType(
        responseProductsApiBySlug[0]
          .releaseType as keyof typeof SHOPIFY_TO_STORYBLOK_RELEASE_TYPE_MAPPER,
      )
    : contentProductStoryblok.releaseType;

  if (
    (releaseType === 'draw-release' || releaseType === 'auction') &&
    launchDate
  ) {
    const parsedLaunchDate = parseDate(launchDate);
    // Older draw's were not supported by the draws service. This prevents loads of not
    // found errors being recorded by Grafana and Sentry.
    if (
      shopifyId &&
      parsedLaunchDate &&
      isAfter(parsedLaunchDate, FIRST_SUPPORTED_DRAW_LAUNCH)
    ) {
      const res = await new AvantArteDrawGateway(client).getDrawByProductId(
        shopifyId,
      );
      if (res) {
        mainDraw = {
          draw: res,
          product: responseProductsApiBySlug[0],
        };

        if (res.multisetID) {
          const linkedDrawsRes = await new AvantArteDrawGateway(
            client,
          ).getDrawsByMultiSet(res.multisetID);
          if (linkedDrawsRes) {
            const products = await aaProductsGatewayServer.getProducts(
              [],
              linkedDrawsRes.map((d) => d.productId),
              [],
            );

            linkedDrawsRes.forEach((d) => {
              if (d.id === res.id) {
                return;
              }
              const product = products.find((p) => p.shopifyId === d.productId);
              if (product) {
                linkedDraws.push({ draw: d, product });
              }
            });
          }
        }
      }
    }
  }

  if (mainDraw) {
    mainDraw = await enrichDrawForSSR(mainDraw);
  }

  if (linkedDraws.length > 0) {
    const enrichedLinkedDraws = await Promise.all(
      linkedDraws.map(enrichDrawForSSR),
    );
    linkedDraws.splice(0, linkedDraws.length, ...enrichedLinkedDraws);
  }

  if (!contentProductStoryblok.productArtist) {
    contentProductStoryblok.productArtist = [];
  }

  const artistsKey = isV2 ? 'artists' : 'productArtist';
  const artists: ArtistPageContent[] =
    contentProductStoryblok[artistsKey].length > 0
      ? contentProductStoryblok[artistsKey].map(
          (a: ResolvedRelatedArtist) => a.artist,
        )
      : [];

  const mappedArtists = artists.map((a) => mapArtistPageContentToArtist(a));

  const [augmentedArtists, internalArtists, worksCount] = await Promise.all([
    augmentArtists(mappedArtists),
    new AvantArteArtistsSearchGateway(client).getArtistsBySlug(
      artists.map((a) => a.slug),
    ),
    fetchAllProductsCount('tag:Listing page'),
  ]);

  // Set internal ID
  contentProductStoryblok[artistsKey] = contentProductStoryblok[artistsKey].map(
    (artist: ResolvedRelatedArtist) => {
      const internalArtistIndex = internalArtists.findIndex(
        (a) => a.slug === artist.artist.slug,
      );

      return {
        ...artist,
        artist: {
          ...artist.artist,
          internalId:
            internalArtistIndex === -1
              ? null
              : internalArtists[internalArtistIndex].id,
        },
      };
    },
  );

  const productMediums =
    contentProductStoryblok.medium as StoryblokDatatypeMedium[];

  const recommendations: Product[] = await fetchGeneralRecommendations(
    augmentedArtists?.[0]?.name,
    productMediums,
  );

  const pricesAndAvailability: PricesAndAvailability = {
    pricesAndCurrencies: [],
    upcoming: false,
    available: true,
  };

  if (shopifyId && productDetails?.tags) {
    const tags = productDetails?.tags;
    if (tags.includes(PRODUCT_TAG_COMING_SOON)) {
      pricesAndAvailability.upcoming = true;
      pricesAndAvailability.available = false;
    }

    if (tags.includes(PRODUCT_TAG_SOLD_OUT)) {
      pricesAndAvailability.available = false;
    }
  }

  const glossary = await fetchGlossary({ locale, preview });

  const description = isV2
    ? ''
    : richTextWithGlossary(
        contentProductStoryblok.details?.[0]?.description as StoryblokRichtext,
        glossary,
      );

  const [productArtist] = isV2
    ? contentProductStoryblok.artists
    : contentProductStoryblok.productArtist;

  const productGlossaryTerms = isV2
    ? []
    : glossary.filter((term) => {
        const terms =
          (
            productArtist?.artist as Exclude<
              typeof productArtist.artist,
              string
            >
          )?.content?.glossary || [];
        return terms.includes(term.uuid);
      });

  const story = responseProductStoryblok?.data?.story ?? false;

  const image = (() => {
    const firstImage = contentProductStoryblok?.images?.[0];

    if (firstImage && firstImage.component === 'image') return firstImage.image;

    return isV2
      ? contentProductStoryblok.ogImage
      : contentProductStoryblok.thumbnail;
  })();

  return {
    props: {
      version: isV2 ? 2 : 1,
      slug,
      aaProduct: responseProductsApiBySlug?.[0] || null,
      productDetails,
      story: {
        ...story,
        content: {
          ...story.content,
          productVariants: variants
            ? [
                {
                  ...story.content.productVariants[0],
                  variants: variants.data.stories,
                },
              ]
            : null,
        },
      },
      pageData: {
        description,
        draws: {
          mainDraw,
          linkedDraws,
        },
        glossary: mapGlossaryTerms(glossary),
        recommendations,
        worksCount,
        augmentedArtists,
        taxText: responseStoryblokFaq,
        pricesAndAvailability,
        productGlossaryTerms: mapGlossaryLinks(productGlossaryTerms),
        reviews: (responseReviews?.data?.reviews || []).slice(0, 8),
      },
      preview,
      locale,
      locales,
      ogImageUrl: `${image.filename}/m/1200x630/filters:format(jpeg)`,
      ...(await pageTranslations(isJa ? 'ja' : locale!, [
        'addresses',
        'artists',
        'framing',
        'nftPrintService',
        'orderDetails',
        'product',
        'web3Product',
        'gatedRelease',
        'og',
      ])),
    },
    revalidate: FIFTEEN_MINUTES_IN_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      { params: { slug: ['ai-weiwei-middle-finger-in-red'], locale: 'en' } },
    ],
    fallback: 'blocking',
  };
};

async function enrichDrawForSSR(draw: AvailableDraw): Promise<DrawWithFrames> {
  const data = await shopifyProductsGateway.getProductPurchaseDetails({
    frameSource: 'currentProduct',
    identifier: { id: draw.draw.productId },
    releaseName: 'draw',
  });

  return {
    ...draw,
    purchaseDetailsEnriched: true,
    product: {
      ...draw.product,
      price: data?.price ?? draw.product.price,
    },
    frames: data?.frames,
    frameRecommendedTitle: data?.frameRecommendedTitle ?? null,
    frameRecommendedLabel: data?.frameRecommendedLabel ?? null,
  };
}

async function fetchGeneralRecommendations(
  artistName: string | undefined,
  productMediums: StoryblokDatatypeMedium[],
) {
  const recommendationsGateway = new ShopifyProductRecommendationsGateway(
    cache,
  );

  // TODO(afr): Update how `getRecommendations` works to support excluding multiple
  // https://linear.app/avantarte/issue/PTE-500/allow-excluding-multiple-artists-from-recommendations-query
  return await recommendationsGateway.getRecommendations(
    artistName,
    productMediums.includes('print')
      ? PRODUCT_TAG_MEDIUM_PRINT
      : productMediums.includes('sculpture')
        ? PRODUCT_TAG_MEDIUM_SCULPTURE
        : productMediums.includes('collectible')
          ? PRODUCT_TAG_MEDIUM_COLLECTIBLE
          : productMediums.includes('nft')
            ? PRODUCT_TAG_MEDIUM_NFT
            : productMediums.includes('studioworks')
              ? PRODUCT_TAG_MEDIUM_STUDIO_WORKS
              : undefined,
  );
}
